import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Snackbar, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { addRecording as addRecordingApi, removeRecording as removeRecordingApi } from '../services/workspaceService';
import { getDataCapture, getDataCapturesList } from '../services/dataCaptureService';
import { getAllRules } from '../services/ruleService';
import LoadingSpinner from '../components/LoadingSpinner';
import useSaveStatus from '../hooks/useSaveStatus';
import {
  UnifiedTimeline,
  WidgetPicker,
  TimelineProvider,
  computeTimelineBounds,
  computeAllEvents,
} from '../sections/@dashboard/workspace';
import { getActiveTab, updateTab, removeRecordingWidgets } from '../sections/@dashboard/workspace/workspaceTabUtils';
import TabBar from '../sections/@dashboard/workspace/TabBar';
import WorkspaceSettingsDrawer from '../sections/@dashboard/workspace/WorkspaceSettingsDrawer';
import WorkspaceToolbar from '../sections/@dashboard/workspace/WorkspaceToolbar';
import WorkspaceTabPanels from '../sections/@dashboard/workspace/WorkspaceTabPanels';
import WorkspaceEmptyState from '../sections/@dashboard/workspace/WorkspaceEmptyState';
import AddRecordingDialog from '../sections/@dashboard/workspace/AddRecordingDialog';
import RemoveRecordingDialog from '../sections/@dashboard/workspace/RemoveRecordingDialog';
import RecordingChipPopover from '../sections/@dashboard/workspace/RecordingChipPopover';
import starterWidgets from '../sections/@dashboard/workspace/workspaceStarterLayout';
import { colorForIndex } from '../sections/@dashboard/workspace/recordingColors';
import useWorkspaceLayout from '../sections/@dashboard/workspace/useWorkspaceLayout';
import useWorkspaceLoader from '../sections/@dashboard/workspace/useWorkspaceLoader';
import useWorkspaceAnnotations from '../sections/@dashboard/workspace/useWorkspaceAnnotations';
import useWorkspaceTabs from '../sections/@dashboard/workspace/useWorkspaceTabs';
import useWorkspaceRules from '../sections/@dashboard/workspace/useWorkspaceRules';
import useTabWidgets from '../sections/@dashboard/workspace/useTabWidgets';

const PREVIEW_DISMISSED_KEY = 'vpsi_workspace_preview_dismissed';
const byStartTime = (a, b) => (a.startTime || 0) - (b.startTime || 0);

/**
 * Coordinates one workspace: what it holds, how it is laid out, and the two
 * flows that change its contents.
 *
 * The state lives in hooks beside this file — useWorkspaceLayout owns the
 * layout and its debounce, useWorkspaceAnnotations the notes/markers/colours,
 * useWorkspaceTabs / useWorkspaceRules / useTabWidgets the handlers over them.
 * What is left here is the part that genuinely needs all of them at once:
 * adding and removing a recording, which touches the workspace, the recording
 * list, the layout and the metadata in one gesture.
 */
export default function WorkspacePage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();

  const { status: saveStatus, track: trackSave } = useSaveStatus();
  const [undoAction, setUndoAction] = useState(null); // { message, onUndo }

  const { layoutConfig, setLayoutConfig, updateLayoutAndPersist } = useWorkspaceLayout(workspaceId, trackSave);
  const annotations = useWorkspaceAnnotations(workspaceId, trackSave);
  const { recordingMetadata, setRecordingMetadata, updateRecordingMetadata, patchRecordingMetadata } = annotations;

  const [workspaceSettings, setWorkspaceSettings] = useState({ skipEmptyTime: false });
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [apiRules, setApiRules] = useState([]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [addRecordingDialogOpen, setAddRecordingDialogOpen] = useState(false);
  const [allRecordings, setAllRecordings] = useState([]);
  const [selectedRecordingIds, setSelectedRecordingIds] = useState([]);
  const [recordingSearch, setRecordingSearch] = useState('');
  const [removeRecordingId, setRemoveRecordingId] = useState(null);

  const [chipPopoverAnchor, setChipPopoverAnchor] = useState(null);
  const [chipPopoverRecId, setChipPopoverRecId] = useState(null);
  const [chipLabelDraft, setChipLabelDraft] = useState('');

  const [showPreviewWarning, setShowPreviewWarning] = useState(() => !localStorage.getItem(PREVIEW_DISMISSED_KEY));
  const dismissPreviewWarning = useCallback(() => {
    setShowPreviewWarning(false);
    localStorage.setItem(PREVIEW_DISMISSED_KEY, 'true');
  }, []);

  // --- Load -----------------------------------------------------------------

  const hydrate = (ws, openingLayout) => {
    setLayoutConfig(openingLayout);
    annotations.setGlobalNotes(ws.notes || '');
    annotations.setGlobalMarkers(ws.timelineMarkers || []);
    annotations.setRecordingMetadata(ws.recordingMetadata || {});
    if (openingLayout.settings) setWorkspaceSettings(openingLayout.settings);
  };

  const { workspace, setWorkspace, recordings, setRecordings, loading } = useWorkspaceLoader({
    workspaceId,
    navigate,
    hydrate,
  });

  useEffect(() => {
    getAllRules()
      .then(setApiRules)
      .catch(() => {});
  }, []);

  // An empty workspace has nothing to show, so ask for its first recording —
  // once, on arrival. Reopening every time the workspace goes empty meant
  // removing the last recording popped the dialog straight over the undo
  // snackbar, putting the undo out of reach of the keyboard and the screen
  // reader. The empty state behind it offers the same route back.
  const askedOnArrival = useRef(false);
  useEffect(() => {
    if (!workspace || loading || askedOnArrival.current) return;
    // Decided once, on the first render after the load settles — so a later
    // removal cannot re-trigger it.
    askedOnArrival.current = true;
    if (recordings.length === 0) {
      getDataCapturesList()
        .then(setAllRecordings)
        .catch(() => {});
      setAddRecordingDialogOpen(true);
    }
  }, [workspace, recordings.length, loading]);

  // --- Derived state --------------------------------------------------------

  const activeTab = useMemo(() => getActiveTab(layoutConfig), [layoutConfig]);

  const { handleSelectTab, handleAddTab, handleRenameTab, handleDeleteTab, handleReorderTabs, handleSetDefaultTab } =
    useWorkspaceTabs({ layoutConfig, updateLayoutAndPersist, offerUndo: setUndoAction });

  const { allRulesPool, activeRules, handleCreateRule, handleToggleGlobalRule, makeToggleTabRule } = useWorkspaceRules({
    layoutConfig,
    activeTab,
    updateLayoutAndPersist,
    apiRules,
    setApiRules,
  });

  const {
    handleAddWidget,
    makeRemoveWidget,
    makeUpdateWidget,
    makeLayoutChange,
    makeUpdateTabNotes,
    makeUpdateTabMarkers,
  } = useTabWidgets({ layoutConfig, activeTab, updateLayoutAndPersist });

  const eventsMap = useMemo(() => computeAllEvents(recordings, activeRules), [recordings, activeRules]);

  // Memoized because TimelineProvider takes both: rebuilding either on every
  // render is work the timeline then has to absorb.
  const bounds = useMemo(() => computeTimelineBounds(recordings), [recordings]);
  const allEvents = useMemo(() => Object.values(eventsMap).flat(), [eventsMap]);

  const allTabMarkers = useMemo(
    () =>
      (layoutConfig.tabs || []).map((tab) => ({
        tabId: tab.id,
        tabName: tab.name,
        markers: tab.timelineMarkers || [],
      })),
    [layoutConfig.tabs]
  );

  const otherTabsRules = useMemo(
    () =>
      (layoutConfig.tabs || [])
        .filter((tab) => tab.id !== layoutConfig.activeTabId)
        .map((tab) => ({ tabId: tab.id, tabName: tab.name, rules: tab.rules || [] })),
    [layoutConfig.tabs, layoutConfig.activeTabId]
  );

  const handleUpdateSettings = useCallback(
    (newSettings) => {
      setWorkspaceSettings(newSettings);
      updateLayoutAndPersist({ ...layoutConfig, settings: newSettings });
    },
    [layoutConfig, updateLayoutAndPersist]
  );

  // --- Adding a recording ---------------------------------------------------

  /** Colour anything new, leaving anything already coloured alone. */
  const colorNewRecordings = (added) => {
    const alreadyColoured = Object.keys(recordingMetadata).length;
    const next = { ...recordingMetadata };
    added.forEach((rec, index) => {
      if (!next[rec.id]) next[rec.id] = { color: colorForIndex(alreadyColoured + index), label: '' };
    });
    return next;
  };

  const handleAddRecordings = async () => {
    if (selectedRecordingIds.length === 0) return;
    const isFirstRecording = recordings.length === 0;
    try {
      let latestWorkspace = workspace;
      const added = [];
      for (const recordingId of selectedRecordingIds) {
        latestWorkspace = await addRecordingApi(workspaceId, recordingId);
        const rec = await getDataCapture(recordingId).catch(() => null);
        if (rec) added.push(rec);
      }
      setWorkspace(latestWorkspace);
      setRecordings((prev) => [...prev, ...added].sort(byStartTime));
      closeAddRecordingDialog();

      updateRecordingMetadata(colorNewRecordings(added));
      toast.success(`${added.length} recording${added.length !== 1 ? 's' : ''} added`);

      // A workspace's first recording gets a starting set of panels; after that
      // the layout is the user's.
      if (isFirstRecording && added.length > 0 && activeTab) {
        const starters = starterWidgets({ hasVideo: added.some((r) => !r.sensorsOnly) });
        updateLayoutAndPersist(
          updateTab(layoutConfig, activeTab.id, {
            widgets: [...(activeTab.widgets || []), ...starters],
          })
        );
      }
    } catch (err) {
      toast.error('Failed to add recording(s)');
    }
  };

  // --- Removing a recording -------------------------------------------------

  const handleRemoveRecording = async (recordingId) => {
    // Snapshot everything the removal destroys so it can be undone.
    const removed = recordings.find((r) => r.id === recordingId);
    const layoutBefore = layoutConfig;
    const metadataBefore = recordingMetadata[recordingId];

    const restore = async () => {
      try {
        setWorkspace(await addRecordingApi(workspaceId, recordingId));
        if (removed) setRecordings((prev) => [...prev, removed].sort(byStartTime));
        updateLayoutAndPersist(layoutBefore);
        if (metadataBefore !== undefined) {
          // Persist, not just re-render: the colour and label used to come back
          // on screen and be gone again on the next load.
          patchRecordingMetadata(recordingId, metadataBefore);
        }
        toast.success('Recording restored');
      } catch (err) {
        toast.error('Failed to restore recording');
      }
    };

    try {
      setWorkspace(await removeRecordingApi(workspaceId, recordingId));
      setRecordings((prev) => prev.filter((r) => r.id !== recordingId));
      // Its widgets go from every tab, not just the one on screen.
      updateLayoutAndPersist(removeRecordingWidgets(layoutConfig, recordingId));
      setRecordingMetadata((prev) => {
        const next = { ...prev };
        delete next[recordingId];
        return next;
      });
      setUndoAction({ message: `Removed "${removed?.name || 'recording'}"`, onUndo: restore });
      toast.success('Recording removed');
    } catch (err) {
      toast.error('Failed to remove recording');
    }
  };

  // --- Dialog and popover plumbing -----------------------------------------

  const openAddRecordingDialog = async () => {
    try {
      setAllRecordings(await getDataCapturesList());
    } catch (err) {
      // An empty list still lets the dialog explain itself.
    }
    setAddRecordingDialogOpen(true);
  };

  function closeAddRecordingDialog() {
    setAddRecordingDialogOpen(false);
    setRecordingSearch('');
    setSelectedRecordingIds([]);
  }

  const openChipPopover = useCallback((recordingId, anchor, label) => {
    setChipPopoverAnchor(anchor);
    setChipPopoverRecId(recordingId);
    setChipLabelDraft(label);
  }, []);

  /** Dismissing commits a label the user typed but never confirmed with Enter. */
  const closeChipPopover = useCallback(() => {
    if (chipPopoverRecId && chipLabelDraft !== (recordingMetadata[chipPopoverRecId]?.label || '')) {
      patchRecordingMetadata(chipPopoverRecId, { label: chipLabelDraft });
    }
    setChipPopoverAnchor(null);
    setChipPopoverRecId(null);
  }, [chipPopoverRecId, chipLabelDraft, recordingMetadata, patchRecordingMetadata]);

  // --- Render ---------------------------------------------------------------

  if (loading) return <LoadingSpinner message="Loading workspace..." />;
  if (!workspace) return null;

  const hasTimeline = bounds.end > bounds.start;
  const availableRecordings = allRecordings
    .filter((r) => !workspace.recordingIds?.includes(r.id))
    .sort((a, b) => (b.startTime || b.created || 0) - (a.startTime || a.created || 0));

  return (
    <>
      <Helmet>
        <title>VPSI | {workspace.name}</title>
      </Helmet>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 24px)',
          mx: { xs: 0, lg: -2 },
          mt: { xs: 0, lg: -4 },
          mb: -4,
        }}
      >
        {showPreviewWarning && (
          <Alert severity="warning" onClose={dismissPreviewWarning} sx={{ flexShrink: 0, borderRadius: 0 }}>
            <AlertTitle>Early Feature Preview</AlertTitle>
            This is a very early feature preview. There might be bugs with the workspaces feature and we are currently
            trying to address them. Please let Kyle know at{' '}
            <a href="mailto:kyle.fischer@videoplussensors.com">kyle.fischer@videoplussensors.com</a> if there are any
            that you come across.
          </Alert>
        )}

        <Typography variant="h4" sx={{ px: 3, mt: 2 }} noWrap>
          {workspace.name}
        </Typography>

        <WorkspaceToolbar
          recordings={recordings}
          recordingMetadata={recordingMetadata}
          saveStatus={saveStatus}
          onOpenChip={openChipPopover}
          onAddRecording={openAddRecordingDialog}
          onAddPanel={() => setPickerOpen(true)}
        />

        <TabBar
          tabs={layoutConfig.tabs || []}
          activeTabId={layoutConfig.activeTabId}
          defaultTabId={layoutConfig.defaultTabId}
          onSelectTab={handleSelectTab}
          onAddTab={handleAddTab}
          onRenameTab={handleRenameTab}
          onDeleteTab={handleDeleteTab}
          onReorderTabs={handleReorderTabs}
          onSetDefault={handleSetDefaultTab}
          onOpenSettings={() => setSettingsDrawerOpen(true)}
        />

        {hasTimeline ? (
          <TimelineProvider
            bounds={bounds}
            recordings={recordings}
            events={allEvents}
            skipEmptyTime={workspaceSettings.skipEmptyTime}
            highlightReelMode={workspaceSettings.highlightReelMode}
            highlightReelLag={workspaceSettings.highlightReelLag ?? 5}
          >
            <WorkspaceTabPanels
              workspaceId={workspaceId}
              tabs={layoutConfig.tabs || []}
              activeTabId={layoutConfig.activeTabId}
              recordings={recordings}
              recordingMetadata={recordingMetadata}
              eventsMap={eventsMap}
              rules={allRulesPool}
              activeRules={activeRules}
              globalRules={layoutConfig.globalRules || []}
              globalActiveRuleIds={layoutConfig.globalActiveRuleIds || []}
              workspaceNotes={annotations.globalNotes}
              workspaceMarkers={annotations.globalMarkers}
              otherTabsRules={otherTabsRules}
              onToggleGlobalRule={handleToggleGlobalRule}
              onCreateRule={handleCreateRule}
              onUpdateNotes={annotations.updateGlobalNotes}
              onUpdateMarkers={annotations.updateGlobalMarkers}
              makeRemoveWidget={makeRemoveWidget}
              makeUpdateWidget={makeUpdateWidget}
              makeLayoutChange={makeLayoutChange}
              makeUpdateTabNotes={makeUpdateTabNotes}
              makeUpdateTabMarkers={makeUpdateTabMarkers}
              makeToggleTabRule={makeToggleTabRule}
            />

            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                flexShrink: 0,
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                bgcolor: 'background.default',
              }}
            >
              <UnifiedTimeline
                recordings={recordings}
                eventsMap={eventsMap}
                globalMarkers={annotations.globalMarkers}
                tabMarkers={activeTab?.timelineMarkers || []}
                activeTabId={layoutConfig.activeTabId}
                allTabMarkers={allTabMarkers}
                onSwitchToTab={handleSelectTab}
                recordingMetadata={recordingMetadata}
              />
            </Box>
          </TimelineProvider>
        ) : (
          <WorkspaceEmptyState onAddRecording={openAddRecordingDialog} />
        )}
      </Box>

      <AddRecordingDialog
        open={addRecordingDialogOpen}
        isFirstRecording={recordings.length === 0}
        available={availableRecordings}
        selectedIds={selectedRecordingIds}
        search={recordingSearch}
        onSearchChange={setRecordingSearch}
        onToggleSelected={(id) =>
          setSelectedRecordingIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]))
        }
        onClose={closeAddRecordingDialog}
        onAdd={handleAddRecordings}
      />

      <RemoveRecordingDialog
        open={removeRecordingId != null}
        recordingName={recordings.find((r) => r.id === removeRecordingId)?.name}
        onCancel={() => setRemoveRecordingId(null)}
        onConfirm={() => {
          handleRemoveRecording(removeRecordingId);
          setRemoveRecordingId(null);
        }}
      />

      <RecordingChipPopover
        anchorEl={chipPopoverAnchor}
        recordingName={recordings.find((r) => r.id === chipPopoverRecId)?.name}
        color={recordingMetadata[chipPopoverRecId]?.color}
        labelDraft={chipLabelDraft}
        onLabelDraftChange={setChipLabelDraft}
        onPickColor={(color) => patchRecordingMetadata(chipPopoverRecId, { color })}
        onCommitLabel={() => patchRecordingMetadata(chipPopoverRecId, { label: chipLabelDraft })}
        onClose={closeChipPopover}
        onRemoveRecording={() => {
          setChipPopoverAnchor(null);
          setRemoveRecordingId(chipPopoverRecId);
          setChipPopoverRecId(null);
        }}
      />

      <WidgetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={handleAddWidget}
        recordings={recordings}
      />

      <WorkspaceSettingsDrawer
        open={settingsDrawerOpen}
        onClose={() => setSettingsDrawerOpen(false)}
        settings={workspaceSettings}
        onUpdateSettings={handleUpdateSettings}
      />

      {/* Undo for the two destructive actions: removing a tab, removing a recording. */}
      <Snackbar
        open={undoAction != null}
        autoHideDuration={8000}
        onClose={() => setUndoAction(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={undoAction?.message}
        action={
          <Button
            color="secondary"
            size="small"
            onClick={() => {
              undoAction?.onUndo?.();
              setUndoAction(null);
            }}
          >
            Undo
          </Button>
        }
      />
    </>
  );
}
